import React from "react";
import PropTypes from "prop-types";
import useStyles from "@hooks/useStyles";
import useStores from "@hooks/useStores";

import Box from "@material-ui/core/Box";
import useStaticContext from "@hooks/useStaticContext";
import Chip from "@material-ui/core/Chip";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import { Link } from "@material-ui/core";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { INLINES, BLOCKS } from "@contentful/rich-text-types";
import ContentAsset from "./ContentAsset";

import { adjective as proAdjective } from "@language/provider";

const { SITE_NAME } = process.env;

const ContentHero = ({ title, hero, service }) => {
	const classes = useStyles("Content");
	const { contentStore: store } = useStores();

	const backgroundImage =
		hero.backgroundImage && hero.backgroundImage[0]?.secure_url;

	// const backgroundImageMobile = backgroundImage?.replace(
	// 	"q_auto",
	// 	"q_auto,w_700"
	// );

	const options = {
		renderNode: {
			[INLINES.HYPERLINK]: (node, children) => {
				return (
					<a href={`${node.data.uri}`} target="_blank">
						{children}
					</a>
				);
			},
			[INLINES.ENTRY_HYPERLINK]: (node, children) => {
				const entry = store.findLinkedEntity(
					node,
					hero.snippet2?.links.entries,
				);
				if (!entry) return;
				return (
					<a href={`${entry.folder}${entry.slug}`} target="_blank">
						{children}
					</a>
				);
			},
			[BLOCKS.EMBEDDED_ASSET]: (node) => {
				const asset = store.findLinkedEntity(node, hero.snippet2?.links.assets);
				if (!asset) return;
				return <ContentAsset {...asset} />;
			},
		},
	};
	const snippet = documentToReactComponents(hero.snippet2?.json, options);
	const { app } = useStaticContext();

	const { city, state } = app.location || {};
	const replaceSize = (src, size) => src.replace("q_auto", `q_auto,${size}`);

	return (
		<React.Fragment>
			<section className={classes.hero}>
				{backgroundImage && (
					<picture className={classes.picture}>
						<source
							srcSet={`${replaceSize(
								backgroundImage,
								"h_200,w_400",
							)} 400w, ${replaceSize(
								backgroundImage,
								"h_400,w_768",
							)} 768w, ${replaceSize(backgroundImage, "w_1200")} 1200w`}
							sizes="100vw"
						/>
						<img
							sizes="100vw"
							srcSet={`${replaceSize(
								backgroundImage,
								"h_200,w_400",
							)} 400w, ${replaceSize(
								backgroundImage,
								"h_400,w_768",
							)} 768w, ${replaceSize(backgroundImage, "w_1200")} 1200w`}
							src={replaceSize(backgroundImage, "w_1200")}
							alt={title}
							className={classes.backgroundImage}
							fetchpriority="high"
						/>
					</picture>
				)}

				<Box
					height="100%"
					display={{ xs: "flex", sm: "none" }}
					alignItems="flex-end"
					position="relative"
					color="#FFF"
					py={4}
					px={4}
					className={classes.pictureGradient}
				>
					<Box display={"flex"} flexDirection={"column"}>
						{city && (
							<Box mb={3}>
								<Chip
									label={`${city}, ${state}`}
									style={{ opacity: 0.6, fontWeight: 700 }}
								/>
							</Box>
						)}

						<h1 className={classes.heroH1}>{title}</h1>
					</Box>
				</Box>

				<Box display={{ xs: "none", sm: "block" }}>
					<Container maxWidth="lg">
						<Box
							width="100%"
							left={0}
							top="50%"
							pr={2}
							pl={2}
							className={classes.heroBox}
						>
							<Box
								borderRadius={12}
								boxShadow={5}
								className={classes.heroBoxContent}
								maxWidth={880}
							>
								{city && (
									<Box mb={3}>
										<Chip
											label={`${city}, ${state}`}
											style={{ fontWeight: 700 }}
										/>
									</Box>
								)}

								<h1 className={classes.heroH1}>{title}</h1>

								{hero.priceRangeCollection?.items?.map((section, index) => (
									<Box
										key={`heroprice-${index}`}
										component={"div"}
										color={"success.main"}
										fontWeight={700}
										fontSize={{ xs: 24, sm: 28 }}
										lineHeight={1.4}
										m={"0px 0px 8px"}
										display={{ xs: "block", md: "flex" }}
										alignItems={"center"}
									>
										<span className={classes.costH2}>{section.priceRange}</span>
										<span className={classes.costH2Unit}>{section.unit}</span>
									</Box>
								))}

								<Box my={6}>
									<Divider light />
								</Box>

								{snippet && (
									<Box mb={5} className={classes.body}>
										{snippet}
									</Box>
								)}

								<Box
									color={"grey.700"}
									fontSize={16}
									fontWeight={400}
									lineHeight={1.5}
									mb={7}
								>
									Get free estimates for your project or view our cost guide
									below:
								</Box>

								<Box display={"flex"} alignItems="center">
									<Button
										//onClick={() => getStartedHandler("backgroundImage")}
										href={
											SITE_NAME === "trustedcare"
												? "/"
												: `${store.service?.url}?utm_source=content&utm_medium=hero&utm_content=content&utm_campaign=hero`
										}
										size={"large"}
										variant="contained"
										color="primary"
										disableElevation
										className={classes.promoBoxButton}
										target="_blank"
										aria-label="Get free estimates"
									>
										Get free estimates
									</Button>
									<Box ml={8}>
										{`Are you a ${proAdjective.singular}?`}
										<Link
											href={`/pro?service=${store.content.service}&utm_source=content&utm_medium=text&utm_content=costguide&utm_campaign=costguide`}
											target={"_blank"}
										>
											{` Get new customers`}
										</Link>
									</Box>
								</Box>
							</Box>
						</Box>
					</Container>
				</Box>
			</section>

			<Box
				display={{ xs: "block", sm: "none" }}
				p={4}
				maxWidth="100%"
				bgcolor="#FFF"
			>
				{hero.priceRangeCollection?.items?.map((section, index) => (
					<Box
						key={`hero-${index}`}
						component={"h2"}
						color={"success.main"}
						fontWeight={700}
						fontSize={{ xs: 24, sm: 28 }}
						lineHeight={1.3}
						m={"0px 0px 12px"}
					>
						{section.priceRange}{" "}
						<span className={classes.costH2Unit}>{section.unit}</span>
					</Box>
				))}

				<Box my={4}>
					<Divider light />
				</Box>

				{snippet && (
					<Box mb={5} className={classes.body}>
						{snippet}
					</Box>
				)}

				<Box
					color={"grey.600"}
					fontSize={14}
					fontWeight={400}
					lineHeight={1.5}
					mb={6}
				>
					Get free estimates for your project or view our cost guide below:
				</Box>

				<div>
					<Button
						href={
							SITE_NAME === "trustedcare"
								? "/"
								: `${store.service?.url}?utm_source=content&utm_medium=hero&utm_content=content&utm_campaign=hero`
						}
						fullWidth
						//onClick={() => getStartedHandler("backgroundImageMobile")}
						size={"large"}
						variant="contained"
						color="primary"
						disableElevation
						target="_blank"
						className={classes.promoBoxButton}
					>
						Get free estimates
					</Button>
				</div>
				<Box mt={5}>
					{`Are you a ${proAdjective.singular}?`}
					<Link
						href={`/pro?service=${store.content.service}&utm_source=content&utm_medium=text&utm_content=costguide&utm_campaign=costguide`}
						target={"_blank"}
					>
						{` Get new customers`}
					</Link>
				</Box>
			</Box>
		</React.Fragment>
	);
};

ContentHero.propTypes = {};
export default ContentHero;
